// Lo que dicen las tarjetas del asistente sobre este módulo, en los cuatro idiomas.
//
// Un contrato con `backend/assistant.js`, como la analítica lo es con
// `backend/analytics.js`: cada frase que aquel fichero arma con `phrase('x.y', …)`
// se busca aquí como `crm.assistantTools.x.y`. Una que falte no rompe nada —la
// tarjeta enseña la frase en inglés que trae el backend—, pero sale en inglés en
// medio de una pantalla en castellano. `tests/unit/assistantTools.test.js` lo
// comprueba, campo por campo de los que una tarjeta puede enseñar.

const assistantTools = {
  es: {
    where: { customer: 'Cliente' },
    field: {
      name: 'Nombre', kind: 'Tipo', status: 'Estado', taxId: 'NIF', email: 'Correo', phone: 'Teléfono',
      city: 'Población', sector: 'Sector', notes: 'Notas', creditLimit: 'Límite de crédito',
      paymentTerms: 'Condiciones de pago', paymentMethod: 'Forma de pago', taxRegime: 'Régimen de IVA',
      vatNumber: 'NIF-IVA', withholdingRate: 'Retención', currency: 'Moneda', rating: 'Valoración',
      website: 'Web', address: 'Dirección', postalCode: 'Código postal', country: 'País',
    },
    value: {
      company: 'Empresa', individual: 'Particular',
      prospect: 'Potencial', active: 'Activo', inactive: 'Inactivo',
    },
    note: {
      noTaxId: 'Sin NIF no se detectará si este cliente ya está dado de alta.',
      nothingDeleted: 'En la cartera no se borra nada: un error se archiva a mano.',
      notOnFile: 'Ese cliente no está en la cartera.',
      nothingToChange: 'No hay nada que cambiar: ya dice eso.',
    },
    create: {
      action: 'Dar de alta un cliente',
      summary: 'Dar de alta «{customer}»',
      outcome: 'Cliente {code} dado de alta: {customer}',
    },
    update: {
      action: 'Cambiar la ficha del cliente',
      summary: 'En {customer}: {changes}',
      summaryNothing: 'En {customer}: nada que cambiar, ya dice eso',
      outcome: '{customer} actualizado',
    },
  },

  ca: {
    where: { customer: 'Client' },
    field: {
      name: 'Nom', kind: 'Tipus', status: 'Estat', taxId: 'NIF', email: 'Correu', phone: 'Telèfon',
      city: 'Població', sector: 'Sector', notes: 'Notes', creditLimit: 'Límit de crèdit',
      paymentTerms: 'Condicions de pagament', paymentMethod: 'Forma de pagament', taxRegime: 'Règim d\'IVA',
      vatNumber: 'NIF-IVA', withholdingRate: 'Retenció', currency: 'Moneda', rating: 'Valoració',
      website: 'Web', address: 'Adreça', postalCode: 'Codi postal', country: 'País',
    },
    value: {
      company: 'Empresa', individual: 'Particular',
      prospect: 'Potencial', active: 'Actiu', inactive: 'Inactiu',
    },
    note: {
      noTaxId: 'Sense NIF no es detectarà si aquest client ja està donat d\'alta.',
      nothingDeleted: 'A la cartera no s\'esborra res: un error s\'arxiva a mà.',
      notOnFile: 'Aquest client no és a la cartera.',
      nothingToChange: 'No hi ha res a canviar: ja diu això.',
    },
    create: {
      action: 'Donar d\'alta un client',
      summary: 'Donar d\'alta «{customer}»',
      outcome: 'Client {code} donat d\'alta: {customer}',
    },
    update: {
      action: 'Canviar la fitxa del client',
      summary: 'A {customer}: {changes}',
      summaryNothing: 'A {customer}: res a canviar, ja diu això',
      outcome: '{customer} actualitzat',
    },
  },

  en: {
    where: { customer: 'Customer' },
    field: {
      name: 'Name', kind: 'Kind', status: 'Status', taxId: 'Tax id', email: 'Email', phone: 'Phone',
      city: 'Town', sector: 'Sector', notes: 'Notes', creditLimit: 'Credit limit',
      paymentTerms: 'Payment terms', paymentMethod: 'Payment method', taxRegime: 'VAT regime',
      vatNumber: 'EU VAT number', withholdingRate: 'Withholding', currency: 'Currency', rating: 'Rating',
      website: 'Website', address: 'Address', postalCode: 'Postcode', country: 'Country',
    },
    value: {
      company: 'Company', individual: 'Private individual',
      prospect: 'Prospect', active: 'Active', inactive: 'Inactive',
    },
    note: {
      noTaxId: 'Without a tax id, a duplicate of this customer will not be caught.',
      nothingDeleted: 'Nothing in the customer file is deleted: a mistake is archived by hand.',
      notOnFile: 'That customer is not on file.',
      nothingToChange: 'Nothing to change: it already says that.',
    },
    create: {
      action: 'File a new customer',
      summary: 'File “{customer}”',
      outcome: 'Customer {code} filed: {customer}',
    },
    update: {
      action: 'Change the customer file',
      summary: 'On {customer}: {changes}',
      summaryNothing: 'On {customer}: nothing to change, it already says that',
      outcome: '{customer} updated',
    },
  },

  fr: {
    where: { customer: 'Client' },
    field: {
      name: 'Nom', kind: 'Type', status: 'Statut', taxId: 'Numéro fiscal', email: 'E-mail', phone: 'Téléphone',
      city: 'Ville', sector: 'Secteur', notes: 'Notes', creditLimit: 'Plafond de crédit',
      paymentTerms: 'Conditions de paiement', paymentMethod: 'Moyen de paiement', taxRegime: 'Régime de TVA',
      vatNumber: 'Numéro de TVA intracommunautaire', withholdingRate: 'Retenue', currency: 'Devise', rating: 'Note',
      website: 'Site web', address: 'Adresse', postalCode: 'Code postal', country: 'Pays',
    },
    value: {
      company: 'Entreprise', individual: 'Particulier',
      prospect: 'Prospect', active: 'Actif', inactive: 'Inactif',
    },
    note: {
      noTaxId: 'Sans numéro fiscal, un doublon de ce client ne sera pas détecté.',
      nothingDeleted: 'Rien n’est supprimé dans le fichier clients : une erreur s’archive à la main.',
      notOnFile: 'Ce client n’existe pas dans le fichier.',
      nothingToChange: 'Rien à changer : c’est déjà le cas.',
    },
    create: {
      action: 'Créer un client',
      summary: 'Créer « {customer} »',
      outcome: 'Client {code} créé : {customer}',
    },
    update: {
      action: 'Modifier la fiche du client',
      summary: 'Sur {customer} : {changes}',
      summaryNothing: 'Sur {customer} : rien à changer, c’est déjà le cas',
      outcome: '{customer} mis à jour',
    },
  },
}

/** Los textos del módulo, con los de las tarjetas del asistente colgados de `<namespace>.assistantTools`. */
export function withAssistantTools(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], assistantTools: assistantTools[locale] },
    }
  }
  return merged
}

export default assistantTools
